"use client";

import { useState } from "react";

import { usePrefersDark } from "@/lib/usePrefersDark";

export type ObservedRainfallRow = {
  kind: "observed_rainfall";
  year: number;
  max_1hr_mmhr: number | null;
  max_1hr_location: string | null;
  max_24hr_mm: number | null;
  max_24hr_location: string | null;
  avg_1hr_mmhr: number | null;
};

export type AnnualTotalPrecipRow = {
  kind: "annual_total_precip";
  period: "historical_adjusted" | "2050s" | "2080s";
  median_mm: number;
  p10_mm?: number;
  p90_mm?: number;
};

export type IdfSummaryRow =
  | {
      kind: "idf_summary";
      duration_class: "short_duration_under_24h";
      label: string;
      increase_2050s_pct: number;
      increase_2080s_pct: number;
    }
  | {
      kind: "idf_summary";
      duration_class: "long_duration_2_to_30d";
      label: string;
      increase_2050s_pct_low: number;
      increase_2050s_pct_high: number;
      increase_2080s_pct_low: number;
      increase_2080s_pct_high: number;
    };

export type IdfEstimateRow = {
  kind: "idf_estimate";
  period: "historical_adjusted" | "2050s" | "2080s";
  duration: string;
  return_period: string;
  depth_mm: number;
};

type DesignThreshold = {
  period: "historical_adjusted" | "2050s" | "2080s";
  label: string;
  intensity_mmhr: number;
};

type Props = {
  observed: ObservedRainfallRow[];
  thresholds: DesignThreshold[];
  height?: number;
};

const VIEW_W = 1000;
const PAD_TOP = 40;
const PAD_BOTTOM = 56;
const PAD_LEFT = 16;
const PAD_RIGHT = 116;

/**
 * Visual-first rainfall chart: each year's city-peak 1-hour intensity is
 * a bar coloured by whether it crosses today's 1-in-100-year design line.
 * Bars at or above that line render in alarm coral — "this year already
 * delivered a storm the City's drainage treats as once-a-century." The
 * 2050s and 2080s 100-year lines float above as dotted ghosts; the gesture
 * is "the design line keeps moving up." No numeric y-ticks: only the three
 * threshold values are labelled, inline at the right edge.
 */
export function ClimateRainfallChart({
  observed,
  thresholds,
  height = 380,
}: Props) {
  const isDark = usePrefersDark();
  const [hover, setHover] = useState<string | null>(null);

  const obs = observed
    .filter((r): r is ObservedRainfallRow & { max_1hr_mmhr: number } =>
      r.max_1hr_mmhr !== null,
    )
    .sort((a, b) => a.year - b.year);

  const ordered: DesignThreshold[] = ["historical_adjusted", "2050s", "2080s"]
    .map((period) => thresholds.find((t) => t.period === period))
    .filter((t): t is DesignThreshold => Boolean(t));
  const today = ordered.find((t) => t.period === "historical_adjusted");

  const allValues = [
    ...obs.map((r) => r.max_1hr_mmhr),
    ...ordered.map((t) => t.intensity_mmhr),
    0,
  ];
  const yMax = Math.max(80, Math.ceil(Math.max(...allValues) / 10) * 10 + 10);
  const yMin = 0;

  const VIEW_H = height;
  const plotTop = PAD_TOP;
  const plotBottom = VIEW_H - PAD_BOTTOM;
  const plotLeft = PAD_LEFT;
  const plotRight = VIEW_W - PAD_RIGHT;
  const plotW = plotRight - plotLeft;
  const plotH = plotBottom - plotTop;

  const yToPx = (v: number) =>
    plotBottom - ((v - yMin) / (yMax - yMin)) * plotH;

  const yearSpan =
    obs.length > 0 ? obs[obs.length - 1].year - obs[0].year : 1;
  const barSlotW = plotW / Math.max(yearSpan + 1, 1);
  const barW = Math.max(barSlotW * 0.72, 2);
  const xForYear = (y: number) =>
    obs.length === 0
      ? plotLeft
      : plotLeft +
        ((y - obs[0].year) / Math.max(yearSpan, 1)) * (plotW - barSlotW) +
        barSlotW / 2;

  // Coral alarm token is theme-stable; the neutral fill flips with theme
  // so muted bars stay readable in both light and dark.
  const ALARM = "var(--coral)";
  const NEUTRAL = isDark ? "rgba(213,213,208,0.42)" : "rgba(58,58,55,0.32)";
  const TODAY_LINE = "var(--coral)";
  const GHOST_LINE = "var(--ink-3)";

  return (
    <div className="cs-chart">
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className="w-full h-auto"
        role="img"
        aria-label={`Calgary annual peak 1-hour rainfall intensity, ${obs[0]?.year ?? ""}-${obs.at(-1)?.year ?? ""}, with historical, 2050s, and 2080s 1-in-100-year design intensities for the airport station. Bars at or above the historical 100-year line are highlighted as alarm.`}
      >
        {/* Threshold ghost lines (drawn first so bars and the today line
            sit above). 2050s and 2080s are dotted reference points; only
            their numeric value + period appears as a label. */}
        {ordered
          .filter((t) => t.period !== "historical_adjusted")
          .map((t) => {
            const y = yToPx(t.intensity_mmhr);
            return (
              <g
                key={t.period}
                tabIndex={0}
                role="img"
                aria-label={`${t.label} 1-in-100-year 1-hour design intensity at YYC: ${t.intensity_mmhr} mm/hr`}
                onFocus={() => setHover(`th-${t.period}`)}
                onBlur={() => setHover(null)}
                onMouseEnter={() => setHover(`th-${t.period}`)}
                onMouseLeave={() => setHover(null)}
                style={{ outline: "none" }}
              >
                {hover === `th-${t.period}` ? (
                  <rect
                    x={plotLeft}
                    y={y - 4}
                    width={plotRight - plotLeft}
                    height={8}
                    fill={GHOST_LINE}
                    opacity={0.18}
                    pointerEvents="none"
                  />
                ) : null}
                <line
                  x1={plotLeft}
                  x2={plotRight}
                  y1={y}
                  y2={y}
                  stroke={GHOST_LINE}
                  strokeWidth={hover === `th-${t.period}` ? 1.6 : 1}
                  strokeDasharray="3 5"
                  opacity={hover === `th-${t.period}` ? 1 : 0.7}
                />
                <text
                  x={plotRight + 8}
                  y={y}
                  fontSize={11}
                  fontFamily="var(--font-feature)"
                  fontWeight={600}
                  fill="var(--ink-2)"
                  dominantBaseline="central"
                >
                  {t.intensity_mmhr.toFixed(1)}
                </text>
                <text
                  x={plotRight + 8}
                  y={y + 13}
                  fontSize={9.5}
                  fontFamily="var(--font-recursive)"
                  fill="var(--ink-3)"
                  dominantBaseline="central"
                >
                  {t.label} 100-yr
                </text>
              </g>
            );
          })}

        {/* Observed bars. Each bar is a keyboard focus target so non-
            mouse readers step through the series via Tab and read the
            same year/value/community as the hover tooltip. */}
        {obs.map((r) => {
          const x = xForYear(r.year);
          const y = yToPx(r.max_1hr_mmhr);
          const h = plotBottom - y;
          const isHover = hover === `obs-${r.year}`;
          const crosses =
            today !== undefined && r.max_1hr_mmhr >= today.intensity_mmhr;
          const fill = crosses ? ALARM : NEUTRAL;
          const labelLoc = r.max_1hr_location
            ? ` at ${r.max_1hr_location}`
            : "";
          return (
            <g
              key={r.year}
              tabIndex={0}
              role="img"
              aria-label={`${r.year}: ${r.max_1hr_mmhr.toFixed(1)} mm/hr 1-hour peak${labelLoc}${crosses ? ` — at or above the current 1-in-100-year design intensity (${today?.label} reference period at YYC)` : ""}`}
              onMouseEnter={() => setHover(`obs-${r.year}`)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(`obs-${r.year}`)}
              onBlur={() => setHover(null)}
              style={{ outline: "none" }}
            >
              <rect
                x={x - barW / 2}
                y={y}
                width={barW}
                height={Math.max(h, 0.5)}
                fill={fill}
                opacity={isHover ? 1 : 0.9}
                rx={1}
              />
              {isHover ? (
                <rect
                  x={x - barW / 2 - 2}
                  y={y - 2}
                  width={barW + 4}
                  height={Math.max(h, 0.5) + 4}
                  fill="none"
                  stroke="var(--ink)"
                  strokeWidth={1.5}
                  rx={2}
                  pointerEvents="none"
                />
              ) : null}
              {isHover ? (
                <>
                  {(() => {
                    // Clamp the tooltip box so it stays inside the SVG
                    // viewBox for bars near either edge — without this,
                    // the first few years' tooltips clip off the left
                    // and the last few clip off the right.
                    const TIP_W = 140;
                    const tipLeft = Math.min(
                      Math.max(x - TIP_W / 2, 4),
                      VIEW_W - TIP_W - 4,
                    );
                    const tipMid = tipLeft + TIP_W / 2;
                    return (
                      <>
                        <rect
                          x={tipLeft}
                          y={y - 38}
                          width={TIP_W}
                          height={32}
                          rx={3}
                          fill="var(--bg)"
                          stroke="var(--rule)"
                          strokeWidth={0.6}
                          opacity={0.96}
                        />
                        <text
                          x={tipMid}
                          y={y - 24}
                          fontSize={11}
                          fontFamily="var(--font-feature)"
                          fontWeight={700}
                          fill="var(--ink)"
                          textAnchor="middle"
                        >
                          {r.year}: {r.max_1hr_mmhr.toFixed(1)} mm/hr
                        </text>
                        {r.max_1hr_location ? (
                          <text
                            x={tipMid}
                            y={y - 11}
                            fontSize={10}
                            fontFamily="var(--font-recursive)"
                            fill="var(--ink-2)"
                            textAnchor="middle"
                          >
                            {r.max_1hr_location}
                          </text>
                        ) : null}
                      </>
                    );
                  })()}
                </>
              ) : null}
            </g>
          );
        })}

        {/* Today's 100-year line: solid, prominent, drawn over the bars
            so the eye reads "bars cross this line". */}
        {today ? (
          <g
            tabIndex={0}
            role="img"
            aria-label={`Calgary's current 1-in-100-year 1-hour design intensity at YYC (${today.label} reference period): ${today.intensity_mmhr} mm/hr`}
            onFocus={() => setHover(`th-${today.period}`)}
            onBlur={() => setHover(null)}
            onMouseEnter={() => setHover(`th-${today.period}`)}
            onMouseLeave={() => setHover(null)}
            style={{ outline: "none" }}
          >
            {hover === `th-${today.period}` ? (
              <rect
                x={plotLeft}
                y={yToPx(today.intensity_mmhr) - 4}
                width={plotRight - plotLeft}
                height={8}
                fill={TODAY_LINE}
                opacity={0.18}
                pointerEvents="none"
              />
            ) : null}
            <line
              x1={plotLeft}
              x2={plotRight}
              y1={yToPx(today.intensity_mmhr)}
              y2={yToPx(today.intensity_mmhr)}
              stroke={TODAY_LINE}
              strokeWidth={hover === `th-${today.period}` ? 2.4 : 1.8}
              opacity={1}
            />
            <text
              x={plotRight + 8}
              y={yToPx(today.intensity_mmhr)}
              fontSize={11}
              fontFamily="var(--font-feature)"
              fontWeight={700}
              fill={TODAY_LINE}
              dominantBaseline="central"
            >
              {today.intensity_mmhr.toFixed(1)}
            </text>
            <text
              x={plotRight + 8}
              y={yToPx(today.intensity_mmhr) + 13}
              fontSize={9.5}
              fontFamily="var(--font-recursive)"
              fill="var(--ink-3)"
              dominantBaseline="central"
            >
              today 100-yr
            </text>
            <text
              x={plotRight + 8}
              y={yToPx(today.intensity_mmhr) + 25}
              fontSize={9}
              fontFamily="var(--font-recursive)"
              fill="var(--ink-3)"
              dominantBaseline="central"
              opacity={0.8}
            >
              {today.label} baseline
            </text>
          </g>
        ) : null}

        {/* Right-edge unit caption sits above the threshold labels so the
            reader knows what mm/hr refers to without an axis. */}
        <text
          x={plotRight + 8}
          y={plotTop - 18}
          fontSize={10}
          fontFamily="var(--font-recursive)"
          fill="var(--ink-3)"
        >
          mm/hr
        </text>

        {/* X-axis: first and last observed-year labels and a subtitle. */}
        {obs.length > 0 ? (
          <>
            <text
              x={xForYear(obs[0].year)}
              y={plotBottom + 16}
              fontSize={11}
              fontFamily="var(--font-recursive)"
              fill="var(--ink-3)"
              textAnchor="start"
            >
              {obs[0].year}
            </text>
            <text
              x={xForYear(obs[obs.length - 1].year)}
              y={plotBottom + 16}
              fontSize={11}
              fontFamily="var(--font-recursive)"
              fill="var(--ink-3)"
              textAnchor="end"
            >
              {obs[obs.length - 1].year}
            </text>
            <text
              x={(xForYear(obs[0].year) + xForYear(obs[obs.length - 1].year)) / 2}
              y={plotBottom + 32}
              fontSize={10}
              fontFamily="var(--font-recursive)"
              fill="var(--ink-3)"
              textAnchor="middle"
            >
              annual peak 1-hour intensity, any gauge in the city&rsquo;s
              monitoring network · ArcGIS Layer 7
            </text>
          </>
        ) : null}
      </svg>
    </div>
  );
}
